import React, { useEffect, useMemo, useState } from "react";
import { fetchCatchmentsV2BySchoolId } from "../domains/catchmentV2/catchmentV2.api";

const LAYER_COLORS = {
  1: { fill: "rgba(13, 148, 136, 0.34)", stroke: "#0f766e" },
  2: { fill: "rgba(251, 113, 133, 0.28)", stroke: "#e11d48" },
  3: { fill: "rgba(99, 102, 241, 0.26)", stroke: "#4f46e5" },
  4: { fill: "rgba(100, 116, 139, 0.18)", stroke: "#64748b" },
};

function latestYear(definitions) {
  const years = (definitions || [])
    .map((def) => Number(def.catchment_year))
    .filter((year) => Number.isFinite(year));
  return years.length ? Math.max(...years) : null;
}

/** Keep a moderate number of points, then soften corners for a cleaner outline. */
function sampleRing(ring, maxPoints = 120) {
  if (!Array.isArray(ring) || ring.length < 4) return [];
  const closed =
    ring.length > 1 &&
    Number(ring[0][0]) === Number(ring[ring.length - 1][0]) &&
    Number(ring[0][1]) === Number(ring[ring.length - 1][1]);
  const body = closed ? ring.slice(0, -1) : ring.slice();
  const step = body.length > maxPoints ? Math.ceil(body.length / maxPoints) : 1;
  const sampled = [];
  for (let i = 0; i < body.length; i += step) {
    const pair = body[i];
    if (!Array.isArray(pair) || pair.length < 2) continue;
    const x = Number(pair[0]);
    const y = Number(pair[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    sampled.push([x, y]);
  }
  if (sampled.length < 3) return [];
  if (
    sampled[0][0] !== sampled[sampled.length - 1][0] ||
    sampled[0][1] !== sampled[sampled.length - 1][1]
  ) {
    sampled.push([sampled[0][0], sampled[0][1]]);
  }
  return sampled;
}

function chaikinClosed(points, iterations = 2) {
  let pts = points.slice(0, -1);
  if (pts.length < 3) return points;
  for (let n = 0; n < iterations; n += 1) {
    const next = [];
    for (let i = 0; i < pts.length; i += 1) {
      const p0 = pts[i];
      const p1 = pts[(i + 1) % pts.length];
      next.push([0.75 * p0[0] + 0.25 * p1[0], 0.75 * p0[1] + 0.25 * p1[1]]);
      next.push([0.25 * p0[0] + 0.75 * p1[0], 0.25 * p0[1] + 0.75 * p1[1]]);
    }
    pts = next;
  }
  pts.push([pts[0][0], pts[0][1]]);
  return pts;
}

function ringToPath(ring, project) {
  const sampled = sampleRing(ring);
  if (sampled.length < 4) return "";
  const projected = sampled
    .map(([lon, lat]) => project(lon, lat))
    .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  if (projected.length < 4) return "";
  if (
    projected[0][0] !== projected[projected.length - 1][0] ||
    projected[0][1] !== projected[projected.length - 1][1]
  ) {
    projected.push([projected[0][0], projected[0][1]]);
  }
  const smooth = chaikinClosed(projected, 2);
  let d = "";
  smooth.forEach(([x, y], index) => {
    d += `${index === 0 ? "M" : " L"}${x.toFixed(1)} ${y.toFixed(1)}`;
  });
  return d ? `${d} Z` : "";
}

function geomToPaths(geometry, project) {
  if (!geometry) return [];
  if (geometry.type === "Polygon") {
    return (geometry.coordinates || []).map((ring) => ringToPath(ring, project)).filter(Boolean);
  }
  if (geometry.type === "MultiPolygon") {
    const out = [];
    (geometry.coordinates || []).forEach((poly) => {
      (poly || []).forEach((ring) => {
        const d = ringToPath(ring, project);
        if (d) out.push(d);
      });
    });
    return out;
  }
  return [];
}

function collectCoords(geometry, sink) {
  if (!geometry) return;
  if (geometry.type === "Polygon") {
    (geometry.coordinates || []).forEach((ring) => {
      (ring || []).forEach((pair) => {
        if (Array.isArray(pair) && pair.length >= 2) sink.push([Number(pair[0]), Number(pair[1])]);
      });
    });
  } else if (geometry.type === "MultiPolygon") {
    (geometry.coordinates || []).forEach((poly) => {
      (poly || []).forEach((ring) => {
        (ring || []).forEach((pair) => {
          if (Array.isArray(pair) && pair.length >= 2) sink.push([Number(pair[0]), Number(pair[1])]);
        });
      });
    });
  }
}

/** Unwrap catchments-v2 bucket → drawable Polygon / MultiPolygon. */
function geometryFromKeyBucket(bucket) {
  const rows = Array.isArray(bucket) ? bucket : bucket?.geometries || [];
  const merged = rows.find((row) => row.geometry_kind === "merged");
  const fc = merged?.geojson;
  if (!fc) return null;
  if (fc.type === "Polygon" || fc.type === "MultiPolygon") return fc;
  if (fc.type === "Feature") return fc.geometry || null;
  if (fc.type === "FeatureCollection") {
    const polys = [];
    (fc.features || []).forEach((feature) => {
      const geometry = feature?.geometry;
      if (!geometry) return;
      if (geometry.type === "Polygon") polys.push(geometry.coordinates);
      if (geometry.type === "MultiPolygon") polys.push(...(geometry.coordinates || []));
    });
    if (!polys.length) return null;
    return polys.length === 1
      ? { type: "Polygon", coordinates: polys[0] }
      : { type: "MultiPolygon", coordinates: polys };
  }
  return null;
}

function layerLabel(def, visual) {
  const priority = Number(def.catchment_priority);
  const fromVisual =
    visual?.labels?.layers?.[priority] ||
    visual?.labels?.layers?.[def.catchment_key];
  if (fromVisual?.title) return fromVisual.title;
  return (
    def.catchment_type_display ||
    def.catchment_key ||
    `Priority ${def.catchment_priority || ""}`
  ).trim();
}

function layerDetail(def, visual) {
  const priority = Number(def.catchment_priority);
  const fromVisual =
    visual?.labels?.layers?.[priority] ||
    visual?.labels?.layers?.[def.catchment_key];
  if (fromVisual?.detail) return fromVisual.detail;
  return def.catchment_boundary_display || "";
}

function OpenFallback({ caption, labels, onOpenMap }) {
  const schoolLabel = labels?.school || "School";
  const outside = labels?.outside || caption || "";
  const vbW = 420;
  const vbH = 260;
  const mapSvg = (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      className="sj-catch-svg sj-catch-svg--map"
      role="img"
      aria-hidden={Boolean(onOpenMap)}
      aria-label={onOpenMap ? undefined : "Open catchment"}
    >
      <defs>
        <pattern id="sj-open-grid" width="22" height="22" patternUnits="userSpaceOnUse">
          <path d="M22 0H0V22" fill="none" stroke="#cfd8dd" strokeWidth="0.8" opacity="0.55" />
        </pattern>
        <pattern id="sj-open-roads" width="64" height="64" patternUnits="userSpaceOnUse">
          <path
            d="M0 20H64M18 0V64M0 48H64M46 0V64"
            fill="none"
            stroke="#b7c4cb"
            strokeWidth="1.1"
            opacity="0.35"
          />
        </pattern>
      </defs>
      <rect x="1" y="1" width={vbW - 2} height={vbH - 2} rx="16" fill="#eef3f5" stroke="#d5e0e4" />
      <rect x="1" y="1" width={vbW - 2} height={vbH - 2} rx="16" fill="url(#sj-open-grid)" opacity="0.55" />
      <rect x="1" y="1" width={vbW - 2} height={vbH - 2} rx="16" fill="url(#sj-open-roads)" opacity="0.5" />
      <ellipse cx={vbW * 0.18} cy={vbH * 0.28} rx={vbW * 0.1} ry={vbH * 0.12} fill="#d7e6dc" opacity="0.45" />
      <ellipse cx={vbW * 0.78} cy={vbH * 0.72} rx={vbW * 0.12} ry={vbH * 0.14} fill="#d7e6dc" opacity="0.4" />
      <ellipse cx={vbW * 0.7} cy={vbH * 0.22} rx={vbW * 0.08} ry={vbH * 0.08} fill="#d5e2ea" opacity="0.45" />
      <circle
        cx={vbW / 2}
        cy={vbH / 2}
        r="58"
        fill="none"
        stroke="#94a3b8"
        strokeWidth="2"
        strokeDasharray="5 7"
        opacity="0.7"
      />
      <circle className="sj-catch-pin-ring" cx={vbW / 2} cy={vbH / 2} r="11" fill="none" stroke="#00515b" strokeWidth="1.4" />
      <circle cx={vbW / 2} cy={vbH / 2} r="5.5" fill="#00515b" />
      <text x={vbW / 2 + 12} y={vbH / 2 + 4} className="sj-catch-svg-label">
        {schoolLabel}
      </text>
    </svg>
  );

  return (
    <figure className={`sj-catch-visual${onOpenMap ? " sj-catch-visual--clickable" : ""}`}>
      <MapHitArea onOpenMap={onOpenMap} label={schoolLabel}>
        {mapSvg}
      </MapHitArea>
      <ol className="sj-catch-legend">
        <li>
          <span className="sj-catch-swatch sj-catch-swatch--school" />
          <span className="sj-catch-legend-copy">
            <strong>{schoolLabel}</strong>
            <span>School location — no closed catchment ring on the map</span>
          </span>
        </li>
        {outside ? (
          <li>
            <span className="sj-catch-swatch sj-catch-swatch--outside" />
            <span className="sj-catch-legend-copy">
              <strong>Open catchment</strong>
              <span>{outside}</span>
            </span>
          </li>
        ) : null}
        {caption && caption !== outside ? (
          <li>
            <span className="sj-catch-swatch sj-catch-swatch--outside" />
            <span className="sj-catch-legend-copy">
              <strong>How it works</strong>
              <span>{caption}</span>
            </span>
          </li>
        ) : null}
      </ol>
    </figure>
  );
}

function ViewOnMapHint() {
  return (
    <span className="sj-catch-open-hint">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
        <line x1="9" y1="3" x2="9" y2="18" />
        <line x1="15" y1="6" x2="15" y2="21" />
      </svg>
      View on map
    </span>
  );
}

function MapHitArea({ onOpenMap, label, children }) {
  if (!onOpenMap) return children;
  return (
    <button
      type="button"
      className="sj-catch-map-btn"
      onClick={onOpenMap}
      aria-label={`Open ${label || "school"} catchment on the map`}
    >
      {children}
      <ViewOnMapHint />
    </button>
  );
}

function InnerOuterSchematic({ visual, onOpenMap }) {
  const inner = visual.layers?.[0]?.label || "Inner";
  const outer = visual.layers?.[1]?.label || "Outer";
  const svg = (
    <svg
      viewBox="0 0 320 180"
      className="sj-catch-svg"
      role="img"
      aria-hidden={Boolean(onOpenMap)}
      aria-label={onOpenMap ? undefined : visual.aria || "Inner and outer catchment"}
    >
      <rect x="8" y="8" width="304" height="164" rx="18" fill="#f7fafc" stroke="#d5dde3" />
      <circle cx="160" cy="96" r="68" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" />
      <circle cx="160" cy="96" r="36" fill="#bbf7d0" stroke="#059669" strokeWidth="2" />
      <circle cx="160" cy="96" r="7" fill="#00515b" />
      <text x="248" y="48" className="sj-catch-svg-caption">
        {outer}
      </text>
      <text x="160" y="100" textAnchor="middle" className="sj-catch-svg-label">
        {inner}
      </text>
      <text x="160" y="164" textAnchor="middle" className="sj-catch-svg-caption">
        School at the centre
      </text>
    </svg>
  );

  return (
    <figure className={`sj-catch-visual${onOpenMap ? " sj-catch-visual--clickable" : ""}`}>
      <MapHitArea onOpenMap={onOpenMap} label="school">
        {svg}
      </MapHitArea>
      <ol className="sj-catch-legend">
        {(visual.layers || []).map((layer, index) => (
          <li key={layer.label}>
            <span
              className="sj-catch-swatch"
              style={{
                background: index === 0 ? "#bbf7d0" : "#dbeafe",
                borderColor: index === 0 ? "#059669" : "#3b82f6",
              }}
            />
            <span className="sj-catch-legend-copy">
              <strong>{layer.label}</strong>
              {layer.detail ? <span>{layer.detail}</span> : null}
            </span>
          </li>
        ))}
        {visual.caption ? (
          <li>
            <span className="sj-catch-swatch sj-catch-swatch--outside" />
            <span className="sj-catch-legend-copy">
              <strong>How to read this</strong>
              <span>{visual.caption}</span>
            </span>
          </li>
        ) : null}
      </ol>
    </figure>
  );
}

function PriorityLayersSchematic({ visual, onOpenMap }) {
  const layers = visual.layers || [];
  const fills = ["#fef3c7", "#d1fae5", "#e0f2fe", "#e2e8f0"];
  const strokes = ["#d97706", "#059669", "#0284c7", "#64748b"];
  const svg = (
    <svg
      viewBox="0 0 320 200"
      className="sj-catch-svg"
      role="img"
      aria-hidden={Boolean(onOpenMap)}
      aria-label={onOpenMap ? undefined : visual.aria || "Priority catchment layers"}
    >
      <rect x="8" y="8" width="304" height="184" rx="18" fill="#f7fafc" stroke="#d5dde3" />
      <rect x="36" y="36" width="248" height="128" rx="14" fill="#e0f2fe" stroke="#0284c7" strokeWidth="1.5" />
      <rect x="58" y="54" width="204" height="92" rx="12" fill="#d1fae5" stroke="#059669" strokeWidth="1.5" />
      <rect x="86" y="72" width="148" height="56" rx="10" fill="#fef3c7" stroke="#d97706" strokeWidth="1.5" />
      <circle cx="160" cy="100" r="7" fill="#00515b" />
      <text x="160" y="188" textAnchor="middle" className="sj-catch-svg-caption">
        Priority first, then wider bands, then open
      </text>
    </svg>
  );

  return (
    <figure className={`sj-catch-visual${onOpenMap ? " sj-catch-visual--clickable" : ""}`}>
      <MapHitArea onOpenMap={onOpenMap} label="school">
        {svg}
      </MapHitArea>
      <ol className="sj-catch-legend">
        {layers.map((layer, index) => (
          <li key={layer.label}>
            <span
              className="sj-catch-swatch"
              style={{
                background: fills[Math.min(index, fills.length - 1)],
                borderColor: strokes[Math.min(index, strokes.length - 1)],
              }}
            />
            <span className="sj-catch-legend-copy">
              <strong>{layer.label}</strong>
              {layer.detail ? <span>{layer.detail}</span> : null}
            </span>
          </li>
        ))}
        {visual.caption ? (
          <li>
            <span className="sj-catch-swatch sj-catch-swatch--outside" />
            <span className="sj-catch-legend-copy">
              <strong>How to read this</strong>
              <span>{visual.caption}</span>
            </span>
          </li>
        ) : null}
      </ol>
    </figure>
  );
}

/** Mini map using the same catchment GeoJSON as the main map, with a simple open fallback. */
export default function CatchmentVisual({ schoolId, visual, school, onOpenMap }) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (visual?.kind !== "map") return undefined;
    setPayload(null);
    setError(false);
    fetchCatchmentsV2BySchoolId(schoolId)
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [schoolId, visual?.kind]);

  const mapModel = useMemo(() => {
    if (!payload) return null;
    const year = latestYear(payload.definitions);
    const defs = (payload.definitions || [])
      .filter((def) => Number(def.catchment_year) === year)
      .filter((def) => String(def.geography_type || "").toLowerCase() !== "open")
      .sort((a, b) => Number(b.catchment_priority || 0) - Number(a.catchment_priority || 0));

    const layers = [];
    const coords = [];
    const geometriesByKey = payload.geometriesByKey || {};

    defs.forEach((def) => {
      const geometry = geometryFromKeyBucket(geometriesByKey[def.catchment_key]);
      if (!geometry) return;
      collectCoords(geometry, coords);
      layers.push({
        key: def.catchment_key || String(def.catchment_priority),
        priority: Number(def.catchment_priority) || layers.length + 1,
        label: layerLabel(def, visual),
        detail: layerDetail(def, visual),
        geometry,
      });
    });

    if (!layers.length || coords.length < 3) return null;

    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;
    coords.forEach(([lon, lat]) => {
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    const pad = 0.08;
    const spanLon = Math.max(maxLon - minLon, 0.01);
    const spanLat = Math.max(maxLat - minLat, 0.01);
    minLon -= spanLon * pad;
    maxLon += spanLon * pad;
    minLat -= spanLat * pad;
    maxLat += spanLat * pad;
    const fitLon = Math.max(maxLon - minLon, 0.01);
    const fitLat = Math.max(maxLat - minLat, 0.01);
    const vbW = 420;
    const vbH = 260;
    const margin = 28;

    function project(lon, lat) {
      const x = margin + ((lon - minLon) / fitLon) * (vbW - margin * 2);
      const y = margin + ((maxLat - lat) / fitLat) * (vbH - margin * 2);
      return [x, y];
    }

    const schoolRow = school || payload.school || {};
    const schoolLon = Number(
      schoolRow.lon ?? schoolRow.lng ?? schoolRow.longitude ?? schoolRow.lng_wgs84
    );
    const schoolLat = Number(
      schoolRow.lat ?? schoolRow.latitude ?? schoolRow.lat_wgs84
    );
    const pin =
      Number.isFinite(schoolLon) && Number.isFinite(schoolLat)
        ? project(schoolLon, schoolLat)
        : [vbW / 2, vbH / 2];

    return {
      year,
      layers: layers.map((layer) => ({
        ...layer,
        paths: geomToPaths(layer.geometry, project),
        colors: LAYER_COLORS[layer.priority] || LAYER_COLORS[4],
      })),
      pin,
      vbW,
      vbH,
      schoolLabel: visual?.labels?.school || "School",
      outsideLabel: visual?.labels?.outside || "",
    };
  }, [payload, school, visual]);

  if (visual?.kind === "open") {
    return <OpenFallback caption={visual.caption} labels={visual.labels} onOpenMap={onOpenMap} />;
  }

  if (visual?.kind === "inner_outer") {
    return <InnerOuterSchematic visual={visual} onOpenMap={onOpenMap} />;
  }

  if (visual?.kind === "priority_layers") {
    return <PriorityLayersSchematic visual={visual} onOpenMap={onOpenMap} />;
  }

  if (visual?.kind !== "map") {
    return null;
  }

  if (error) {
    return (
      <figure className="sj-catch-visual">
        <figcaption>Could not load the catchment map for this school.</figcaption>
      </figure>
    );
  }

  if (!mapModel) {
    if (!payload) {
      return (
        <figure className="sj-catch-visual sj-catch-visual--loading">
          <div className="sj-catch-loading">Loading catchment map…</div>
          {visual?.caption ? <figcaption>{visual.caption}</figcaption> : null}
        </figure>
      );
    }
    return (
      <figure className="sj-catch-visual">
        <figcaption>Could not draw the catchment map for this school.</figcaption>
      </figure>
    );
  }

  const mapSvg = (
    <svg
      viewBox={`0 0 ${mapModel.vbW} ${mapModel.vbH}`}
      className="sj-catch-svg sj-catch-svg--map"
      role="img"
      aria-hidden={Boolean(onOpenMap)}
      aria-label={onOpenMap ? undefined : visual?.aria || "School catchment map"}
    >
      <defs>
        <pattern id="sj-map-grid" width="22" height="22" patternUnits="userSpaceOnUse">
          <path d="M22 0H0V22" fill="none" stroke="#cfd8dd" strokeWidth="0.8" opacity="0.55" />
        </pattern>
        <pattern id="sj-map-roads" width="64" height="64" patternUnits="userSpaceOnUse">
          <path
            d="M0 20H64M18 0V64M0 48H64M46 0V64"
            fill="none"
            stroke="#b7c4cb"
            strokeWidth="1.1"
            opacity="0.35"
          />
        </pattern>
      </defs>
      <rect
        x="1"
        y="1"
        width={mapModel.vbW - 2}
        height={mapModel.vbH - 2}
        rx="16"
        fill="#eef3f5"
        stroke="#d5e0e4"
      />
      <rect
        x="1"
        y="1"
        width={mapModel.vbW - 2}
        height={mapModel.vbH - 2}
        rx="16"
        fill="url(#sj-map-grid)"
        opacity="0.55"
      />
      <rect
        x="1"
        y="1"
        width={mapModel.vbW - 2}
        height={mapModel.vbH - 2}
        rx="16"
        fill="url(#sj-map-roads)"
        opacity="0.5"
      />
      <ellipse
        cx={mapModel.vbW * 0.18}
        cy={mapModel.vbH * 0.28}
        rx={mapModel.vbW * 0.1}
        ry={mapModel.vbH * 0.12}
        fill="#d7e6dc"
        opacity="0.45"
      />
      <ellipse
        cx={mapModel.vbW * 0.78}
        cy={mapModel.vbH * 0.72}
        rx={mapModel.vbW * 0.12}
        ry={mapModel.vbH * 0.14}
        fill="#d7e6dc"
        opacity="0.4"
      />
      <ellipse
        cx={mapModel.vbW * 0.7}
        cy={mapModel.vbH * 0.22}
        rx={mapModel.vbW * 0.08}
        ry={mapModel.vbH * 0.08}
        fill="#d5e2ea"
        opacity="0.45"
      />
      {mapModel.layers.map((layer) =>
        layer.paths.map((d, index) => (
          <path
            key={`${layer.key}-${index}`}
            className="sj-catch-shape"
            style={{ animationDelay: `${Math.max(0, 4 - layer.priority) * 90}ms` }}
            d={d}
            fill={layer.colors.fill}
            stroke={layer.colors.stroke}
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))
      )}
      <g className="sj-catch-pin">
        <circle
          className="sj-catch-pin-ring"
          cx={mapModel.pin[0]}
          cy={mapModel.pin[1]}
          r="11"
          fill="none"
          stroke="#00515b"
          strokeWidth="1.4"
        />
        <circle cx={mapModel.pin[0]} cy={mapModel.pin[1]} r="5.5" fill="#00515b" />
        <text
          x={mapModel.pin[0] + 12}
          y={mapModel.pin[1] + 4}
          className="sj-catch-svg-label"
        >
          {mapModel.schoolLabel}
        </text>
      </g>
    </svg>
  );

  return (
    <figure className={`sj-catch-visual${onOpenMap ? " sj-catch-visual--clickable" : ""}`}>
      {onOpenMap ? (
        <button
          type="button"
          className="sj-catch-map-btn"
          onClick={onOpenMap}
          aria-label={`Open ${mapModel.schoolLabel} catchment on the map`}
        >
          {mapSvg}
          <ViewOnMapHint />
        </button>
      ) : (
        mapSvg
      )}
      <ol className="sj-catch-legend">
        <li>
          <span className="sj-catch-swatch sj-catch-swatch--school" />
          <span className="sj-catch-legend-copy">
            <strong>{mapModel.schoolLabel}</strong>
            <span>School location on the map</span>
          </span>
        </li>
        {mapModel.layers
          .slice()
          .sort((a, b) => a.priority - b.priority)
          .map((layer) => (
            <li key={layer.key}>
              <span
                className="sj-catch-swatch"
                style={{ background: layer.colors.fill, borderColor: layer.colors.stroke }}
              />
              <span className="sj-catch-legend-copy">
                <strong>{layer.label}</strong>
                {layer.detail ? <span>{layer.detail}</span> : null}
              </span>
            </li>
          ))}
        {mapModel.outsideLabel ? (
          <li>
            <span className="sj-catch-swatch sj-catch-swatch--outside" />
            <span className="sj-catch-legend-copy">
              <strong>Outside these areas</strong>
              <span>{mapModel.outsideLabel}</span>
            </span>
          </li>
        ) : null}
      </ol>
    </figure>
  );
}
